import { resolvePunctuationLineBreak } from '@src/editor/services/captionLineBreakStyle';
import {
  deriveDubCaptionClips,
  dubCaptionInputs,
  dubRowOptions,
  dubWordIdentityResolver,
  reconcileDubRecord,
} from '@src/editor/services/dubCaptionRows';
import { normalizeLangKey } from '@src/utility/bcp47';

import { KaraokeClip } from '../models/karaoke/KaraokeClip';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { adoptTranslatedCaptionRows } from '../services/adoptTranslatedCaptionRows';
import {
  keepPhraseSoftBreaks,
  keepTokenSoftBreaks,
} from '../services/captionSoftBreaks';
import { IdGenerator } from '../services/IdGenerator';
import {
  joinCaptionRowTexts,
  resolveConfiguredMaxCharLength,
  resolveOverlaps,
  segmentTokens,
  squashOrphanedSegments,
} from '../services/karaokeSegmentation';
import {
  isPhraseDerivedRow,
  reconcileTranslatedCaptionRows,
} from '../services/reconcileTranslatedCaptionRows';

import type { KaraokeConfig } from '../models/karaoke/KaraokeConfig';
import type {
  KaraokeTrack,
  SourceCaptionSet,
} from '../models/karaoke/KaraokeTrack';
import type { TranscriptToken } from '../types';
import type { VideoEditor } from '../VideoEditor';
import type { TranslationPhrase } from '@src/types.ts/translation';

export const ORPHANED_WORDS_COUNT = 2;

/** Caption-config language sentinel meaning "no translation" (matches the producer). */
const ORIGINAL_LANGUAGE = 'original';

export class KaraokeTrackManager {
  private editor: VideoEditor;

  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Reconciles the karaoke track with the current state of transcript tokens.
   * Called after transcript track modifications to ensure karaoke clips remain in sync.
   *
   * Algorithm:
   * - Get new state of active transcript tokens
   * - Get current karaoke track
   * - Create list for updated karaoke clips
   * - For each word in new transcript:
   *   - Move word by word to updated karaoke clips
   *   - If last word of current karaoke clip met or word from another karaoke clip id met,
   *     current karaoke clip should be ended and new clip started with new autogenerated Id,
   *     but old clip id should be still used to detect end of clip
   *   - If word that is not part of any karaoke clip is met it can be added to current clip.
   *     If it is the first word in a new clip, this clip gets new id autogenerated
   *   - The clip ids that didn't exist or extended with new words should be stored in arrays
   *     for further post-processing
   * - Start/end offsets should only be carried over if first/last word of clip is preserved
   * - hiddenTokenIds should be preserved, unless token is not in clip anymore
   *
   * PLAT-11905 — two steps, both run on every call. The dub-timed set, when it
   * is shown, is re-derived from the current dub (`reconcileDubSet`); the
   * source-timed set, shown or parked, is reconciled by the token walk below, so
   * transcript edits made while Dubbed keep the parked set current too.
   */
  public reconcileKaraokeTrack(): void {
    // Get current karaoke track
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (!karaokeTrack) {
      return; // No karaoke track to reconcile
    }

    // Before the source step's "no transcript tokens → clear" branch, which is
    // the source set's alone.
    if (karaokeTrack.liveKind === 'dub') this.reconcileDubSet(karaokeTrack);

    karaokeTrack.sourceSet = this.reconcileSourceSet(karaokeTrack.sourceSet);
  }

  /**
   * PLAT-11905 — re-fit only the dub-timed set, when it is shown. What a caption
   * set switch and a description arriving need: the source-timed set is not
   * touched, so a switch never runs the token walk (which empties a set when the
   * transcript has no active words) where `origin/dev` ran none.
   */
  public reconcileDubCaptions(): void {
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack?.liveKind === 'dub') this.reconcileDubSet(karaokeTrack);
  }

  /**
   * The dub-timed set: its record brought up to date with the CURRENT dub, and
   * its captions derived again from it. Nothing heard, or no kept stretch (a
   * reconcile can run before the clip's own picture is known), leaves the record
   * and the shown captions untouched.
   */
  private reconcileDubSet(karaokeTrack: KaraokeTrack): void {
    const record = karaokeTrack.dubRecord;
    const heard = this.editor.getHeardDub();
    const config = this.editor.getDefaultKaraokeConfig();
    if (!record || !heard || !config) return;
    if (
      normalizeLangKey(record.language) !== normalizeLangKey(heard.language)
    ) {
      return;
    }
    const inputs = dubCaptionInputs(heard);
    const next = reconcileDubRecord(record, inputs, dubRowOptions(config));
    karaokeTrack.dubRecord = next;
    const configs = this.editor.getKaraokeConfigs();
    karaokeTrack.showDubSet(
      deriveDubCaptionClips(
        next,
        inputs,
        configId => (configId ? configs.get(configId) : undefined) ?? config,
        dubWordIdentityResolver(
          inputs,
          this.editor.getTranslationPhrasesForLanguage(config.language) ?? [],
          this.editor.getFullTranscript(),
        ),
      ),
    );
  }

  /** The source-timed set's reconcile: today's token walk, on the given set. */
  private reconcileSourceSet(set: SourceCaptionSet): SourceCaptionSet {
    // Get new state of active transcript tokens
    const activeTranscriptTokens = this.editor.getActiveTranscriptNew();
    if (!activeTranscriptTokens || activeTranscriptTokens.length === 0) {
      // No transcript tokens, clear karaoke track. Break records go too: every
      // record points at a phrase whose source words are all gone, so none could
      // ever be matched or undone again.
      set.clips.length = 0;
      return { clips: set.clips, syntheticBreaks: [] };
    }

    // Phrase-derived (translated) rows are handled separately and are NOT
    // re-partitioned: see `reconcileTranslatedCaptionRows` for why the token walk
    // below structurally cannot own them. Their tokens are then withheld from the
    // walk, so the two paths never contend for the same word.
    const translated = reconcileTranslatedCaptionRows(
      set.clips,
      activeTranscriptTokens,
      set.syntheticBreaks,
    );

    // Step 2 — translated tracks only. `reconcileTranslatedCaptionRows` only
    // keeps or shrinks existing rows; it never creates one. But a restore can
    // bring back a word whose phrase survives in NO row (its row was deleted
    // whole, or all its words were removed then restored). That word has a
    // translation and nothing to show it — left to the walk below it becomes a
    // SOURCE-language row inside a translated track. So ADOPT such words into
    // phrase-aligned translated rows instead, and withhold their tokens from the
    // walk. A phrase already owned by a surviving row is NOT adopted (the row
    // already absorbed its restored boundary word), so a phrase spanning the seam
    // between an existing clip and a new one is never duplicated.
    const translatedPhrases = this.getTranslatedTrackPhrases(set.clips);
    const adoptedRows = translatedPhrases
      ? adoptTranslatedCaptionRows(
          translated.rows,
          activeTranscriptTokens,
          translatedPhrases,
          this.getDefaultKaraokeConfig(),
        )
      : [];

    // Adopted rows own their words too — clone the set only when there is
    // something to add so the shared instance from reconcile is never mutated.
    const ownedTokenIds = adoptedRows.length
      ? new Set<string>(translated.ownedTokenIds)
      : translated.ownedTokenIds;
    for (const row of adoptedRows) {
      for (const token of row.transcriptTokens) ownedTokenIds.add(token.id);
    }

    const remainingTokens = ownedTokenIds.size
      ? activeTranscriptTokens.filter(token => !ownedTokenIds.has(token.id))
      : activeTranscriptTokens;

    // `segmentTokensByPhrase` buckets only `text` tokens, so a translated row's
    // `transcriptTokens` holds no punctuation or whitespace — those belong to no
    // clip at all, their marks having been folded into the row's text. Withholding
    // the rows' words can therefore leave nothing here but punctuation and
    // whitespace, and the walk would build caption rows out of it (rows rendering
    // " ." between the real ones). A row needs a word: with no `text` token left
    // there is nothing for the walk to do.
    const tokenDerivedTokens = remainingTokens.some(
      token => token.type === 'text',
    )
      ? remainingTokens
      : [];

    // Build a map of old karaoke clips by token IDs for fast lookup. Only
    // token-derived clips take part; a phrase-derived row's tokens are already
    // spoken for.
    const oldClipsByTokenId = this.buildTokenToClipMap(
      set.clips.filter(clip => !isPhraseDerivedRow(clip)),
    );

    // Create list for updated karaoke clips
    const updatedClips: KaraokeClip[] = [];
    const newOrExtendedClipIds: string[] = [];
    const modifiedClipIds: string[] = [];

    let currentClipTokens: TranscriptToken[] = [];
    let currentClipId: string | null = null;
    let currentClipConfig: KaraokeConfig | null = null;
    let currentClipHiddenTokenIds: string[] = [];
    let currentClipStartOffset: number | undefined = undefined;
    let currentClipEndOffset: number | undefined = undefined;
    let firstTokenInClipId: string | null = null;
    let lastTokenInClipId: string | null = null;
    let lastTokenOfPreviousClipAdded: boolean = false;

    // Process each token in the new transcript
    for (let i = 0; i < tokenDerivedTokens.length; i++) {
      const token = tokenDerivedTokens[i];
      const oldClip = oldClipsByTokenId.get(token.id);

      // Determine if we need to start a new clip
      const shouldStartNewClip =
        this.shouldStartNewClip(currentClipId, oldClip) ||
        (lastTokenOfPreviousClipAdded && token.type === 'text'); // append punctuation

      if (shouldStartNewClip) {
        // Finalize current clip if it has tokens
        if (currentClipTokens.length > 0) {
          this.finalizeClip(
            updatedClips,
            currentClipTokens,
            currentClipId!,
            currentClipConfig!,
            currentClipHiddenTokenIds,
            currentClipStartOffset,
            currentClipEndOffset,
            firstTokenInClipId,
            lastTokenInClipId,
            oldClipsByTokenId,
            newOrExtendedClipIds,
            modifiedClipIds,
          );
        }

        // Start a new clip
        if (oldClip) {
          // Continue with existing clip
          currentClipId = oldClip.id;
          currentClipConfig = oldClip.karaokeConfig;
          currentClipHiddenTokenIds = [...oldClip.hiddenTranscriptTokenIds];
          firstTokenInClipId = oldClip.transcriptTokens[0]?.id || null;
          lastTokenInClipId =
            oldClip.transcriptTokens[oldClip.transcriptTokens.length - 1]?.id ||
            null;
        } else {
          // Create new clip
          currentClipId = IdGenerator.shortRandomId();
          // todo: logic to choose config?
          currentClipConfig = this.getDefaultKaraokeConfig();
          currentClipHiddenTokenIds = [];
          firstTokenInClipId = null;
          lastTokenInClipId = null;
          newOrExtendedClipIds.push(currentClipId);
        }

        lastTokenOfPreviousClipAdded = false;
        currentClipTokens = [];
        currentClipStartOffset = undefined;
        currentClipEndOffset = undefined;
      }

      // Add token to current clip
      currentClipTokens.push(token);

      // Preserve hidden token IDs if token was in the old clip
      if (oldClip && oldClip.hiddenTranscriptTokenIds.includes(token.id)) {
        if (!currentClipHiddenTokenIds.includes(token.id)) {
          currentClipHiddenTokenIds.push(token.id);
        }
      }

      // Track if this is a new word added to an existing clip
      if (oldClip && !oldClip.transcriptTokens.find(t => t.id === token.id)) {
        if (!newOrExtendedClipIds.includes(currentClipId!)) {
          newOrExtendedClipIds.push(currentClipId!);
        }
      }

      // Update start offset if this is the first token and matches original clip's first token
      if (
        currentClipTokens.length === 1 &&
        oldClip &&
        firstTokenInClipId === token.id
      ) {
        currentClipStartOffset = oldClip.startSecondsOffset;
      }

      // Update end offset if this token matches the original clip's last token
      if (oldClip && lastTokenInClipId === token.id) {
        currentClipEndOffset = oldClip.endSecondsOffset;
        lastTokenOfPreviousClipAdded = true;
      }
    }

    // Finalize the last clip
    if (currentClipTokens.length > 0) {
      this.finalizeClip(
        updatedClips,
        currentClipTokens,
        currentClipId!,
        currentClipConfig!,
        currentClipHiddenTokenIds,
        currentClipStartOffset,
        currentClipEndOffset,
        firstTokenInClipId,
        lastTokenInClipId,
        oldClipsByTokenId,
        newOrExtendedClipIds,
        modifiedClipIds,
      );
    }

    // Interleave the paths back into transcript order, keyed on each row's first
    // surviving token. A split pair shares that key, so a STABLE sort is what
    // keeps the left half ahead of the right.
    const allClips = this.orderClipsByTranscript(
      [...translated.rows, ...adoptedRows, ...updatedClips],
      activeTranscriptTokens,
    );

    // Post-passes run on the COMBINED list, not just the walk's output: a
    // replacement's `state: 'added'` clip has to be able to merge into the row
    // that follows it even when that row is phrase-derived, or the replacement
    // words would strand as their own SOURCE-language row inside a translated
    // track. `prependClip` mutates the target in place, so the row keeps its
    // translation, and `splitClipsIntoSegments` already refuses to re-segment a
    // translated row.
    if (newOrExtendedClipIds.length > 0) {
      // Fix isolated clips from token replacements
      this.mergeIsolatedClipsIntoFollowing(allClips, newOrExtendedClipIds);
      // Post-processing: Split new or extended clips into smaller segments
      this.splitClipsIntoSegments(allClips, newOrExtendedClipIds);
    }

    // Validate and fix overlaps before replacing clips
    resolveOverlaps(allClips);

    // Replace karaoke clips with updated clips. A break record whose phrase
    // lost every source word can never be matched or undone again; drop it
    // rather than let it accumulate.
    return {
      clips: allClips,
      syntheticBreaks: translated.retiredBreakIds.size
        ? set.syntheticBreaks.filter(
            record => !translated.retiredBreakIds.has(record.id),
          )
        : set.syntheticBreaks,
    };
  }

  /**
   * Resolve the active translation phrases when THIS track should be reconciled
   * as translated — its configured caption language is a translation AND it
   * already carries at least one phrase-derived row.
   *
   * The second guard is load-bearing: it stops an original-language (or freshly
   * cleared) track from being silently translated. Adopt only ever ADDS
   * translated rows next to existing ones, so a track with no translated rows has
   * nothing to add to and must fall through to the original-language walk.
   */
  private getTranslatedTrackPhrases(
    clips: readonly KaraokeClip[],
  ): readonly TranslationPhrase[] | null {
    const language = this.editor.getDefaultKaraokeConfig()?.language;
    if (!language || language === ORIGINAL_LANGUAGE) return null;
    if (!clips.some(isPhraseDerivedRow)) return null;
    const phrases = this.editor.getTranslationPhrasesForLanguage?.(language);
    return phrases && phrases.length > 0 ? phrases : null;
  }

  /**
   * Stable-sort clips into transcript order, keyed on each clip's first token.
   * A split pair shares that key, so the stable sort keeps the left half ahead.
   */
  private orderClipsByTranscript(
    clips: KaraokeClip[],
    activeTranscriptTokens: readonly TranscriptToken[],
  ): KaraokeClip[] {
    const orderOf = new Map<string, number>();
    activeTranscriptTokens.forEach((token, index) => {
      if (!orderOf.has(token.id)) orderOf.set(token.id, index);
    });
    const positionOf = (clip: KaraokeClip): number =>
      orderOf.get(clip.transcriptTokens[0]?.id ?? '') ??
      Number.MAX_SAFE_INTEGER;
    return [...clips].sort((a, b) => positionOf(a) - positionOf(b));
  }

  /**
   * Builds a map of token IDs to karaoke clips for fast lookup
   */
  private buildTokenToClipMap(clips: KaraokeClip[]): Map<string, KaraokeClip> {
    const map = new Map<string, KaraokeClip>();
    for (const clip of clips) {
      for (const token of clip.transcriptTokens) {
        map.set(token.id, clip);
      }
    }
    return map;
  }

  /**
   * Determines if a new clip should be started based on current state
   */
  private shouldStartNewClip(
    currentClipId: string | null,
    oldClip: KaraokeClip | undefined,
  ): boolean {
    // Start first clip
    if (currentClipId === null) {
      return true;
    }

    // Start new clip if token belongs to a different clip
    if (oldClip && oldClip.id !== currentClipId) {
      return true;
    }

    return false;
  }

  /**
   * Finalizes a clip and adds it to the updated clips array
   */
  private finalizeClip(
    updatedClips: KaraokeClip[],
    tokens: TranscriptToken[],
    clipId: string,
    config: KaraokeConfig,
    hiddenTokenIds: string[],
    startOffset: number | undefined,
    endOffset: number | undefined,
    firstTokenInClipId: string | null,
    lastTokenInClipId: string | null,
    oldClipsByTokenId: Map<string, KaraokeClip>,
    newOrExtendedClipIds: string[],
    modifiedClipIds: string[],
  ): void {
    // Check if first/last words are preserved
    const firstTokenId = tokens[0]?.id;
    const lastTokenId = tokens[tokens.length - 1]?.id;

    const preserveStartOffset = firstTokenInClipId === firstTokenId;
    const preserveEndOffset = lastTokenInClipId === lastTokenId;

    // Filter hidden token IDs to only include tokens still in the clip
    const tokenIdsSet = new Set(tokens.map(t => t.id));
    const filteredHiddenTokenIds = hiddenTokenIds.filter(id =>
      tokenIdsSet.has(id),
    );

    // The old clip this rebuild continues, if any — the same clip id means the
    // rebuilt instance IS that clip, so its state carries over (below).
    const oldClip = oldClipsByTokenId.get(firstTokenId);
    const sameClip = oldClip?.id === clipId ? oldClip : undefined;

    // Check if clip was modified
    if (sameClip) {
      // Check if token list changed
      const oldTokenIds = sameClip.transcriptTokens.map(t => t.id).join(',');
      const newTokenIds = tokens.map(t => t.id).join(',');
      if (oldTokenIds !== newTokenIds) {
        modifiedClipIds.push(clipId);
      }
    }

    // Create new karaoke clip
    const clip = new KaraokeClip({
      id: clipId,
      transcriptTokens: tokens,
      hiddenTranscriptTokenIds: filteredHiddenTokenIds,
      startSecondsOffset: preserveStartOffset ? startOffset : undefined,
      endSecondsOffset: preserveEndOffset ? endOffset : undefined,
      karaokeConfig: config,
      track: KARAOKE_TRACK_NUMBER,
      ...(sameClip
        ? this.carryOverClipState(sameClip, tokens, tokenIdsSet)
        : {}),
    });

    updatedClips.push(clip);
  }

  /**
   * Carries a surviving clip's baked + user-edited state onto its rebuilt
   * instance.
   *
   * Reconcile recreates every KaraokeClip from the transcript tokens, so
   * anything that is NOT derivable from those tokens is lost unless copied
   * across. For translated captions that loss is silent and destructive:
   * `translatedText` exists only because `ProduceKaraokeCommand` baked the
   * resolved phrases onto the clip, and `KaraokeClip.getKaraokeText()` falls
   * back to joining the source tokens the moment it's gone — so the rendered
   * captions revert to the ORIGINAL language while `KaraokeConfig.language`
   * still says otherwise, and the next save persists that via
   * `toKaraokeLine()`. `SyncTranslatedCaptionTextCommand` can't repair it
   * either: it matches rows by `translatedPhrases[].originalIndices`, which
   * the wipe removed. This runs on every element add/remove/shift/trim
   * (`AbstractCommand.recalculateTimeline`), so a plain "toggle the end card
   * off" was enough to drop a translation.
   *
   * Phrase entries whose source words are all gone are dropped, and
   * `translatedText` is recomposed from the survivors with the same space join
   * the split/merge commands use. `siblingGroupId` (merged-row grouping) and
   * `ignoredByFiltersIds` (words the user restored past a config filter) are
   * carried for the same reason.
   */
  private carryOverClipState(
    oldClip: KaraokeClip,
    tokens: TranscriptToken[],
    tokenIdsSet: Set<string>,
  ): Partial<KaraokeClip> {
    const carried: Partial<KaraokeClip> = {
      ignoredByFiltersIds: oldClip.ignoredByFiltersIds.filter(id =>
        tokenIdsSet.has(id),
      ),
      siblingGroupId: oldClip.siblingGroupId,
      softBreakAfterTokenIds: keepTokenSoftBreaks(
        oldClip.softBreakAfterTokenIds,
        tokens,
      ),
    };

    if (oldClip.translatedText == null) {
      return carried;
    }

    // Phrase identity is by source-word index (`TranslationPhrase.originalIndices`
    // ↔ `TranscriptToken.initial_index`), the same join the phrase segmentation
    // uses.
    const survivingSourceIndices = new Set<number>();
    for (const token of tokens) {
      if (token.type !== 'text') continue;
      survivingSourceIndices.add(token.initial_index);
    }
    // An entry with no `originalIndices` can't be checked — keep it rather than
    // silently dropping text we can't prove is stale.
    const survivingPhrases = oldClip.translatedPhrases?.filter(
      phrase =>
        !phrase.originalIndices?.length ||
        phrase.originalIndices.some(index => survivingSourceIndices.has(index)),
    );

    if (!survivingPhrases) {
      // No per-phrase breakdown (older data): the whole row is one unit.
      return {
        ...carried,
        translatedText: oldClip.translatedText,
        softBreakPhrasePositions: oldClip.softBreakPhrasePositions,
      };
    }
    if (survivingPhrases.length === 0) {
      // Every source word behind this row's translation is gone; let the clip
      // fall back to its tokens instead of rendering stale text.
      return carried;
    }

    const survivingOwnIndices = oldClip.phraseOriginalIndices?.filter(index =>
      survivingSourceIndices.has(index),
    );

    return {
      ...carried,
      translatedText: joinCaptionRowTexts(
        survivingPhrases.map(phrase => phrase.text),
      ),
      translatedPhrases: survivingPhrases.map(phrase => ({ ...phrase })),
      // Normalised back to undefined when empty — the synthetic-break commands
      // treat "no phrase identity" as absent, not as an empty match set.
      phraseOriginalIndices: survivingOwnIndices?.length
        ? survivingOwnIndices
        : undefined,
      softBreakPhrasePositions: keepPhraseSoftBreaks(
        oldClip.softBreakPhrasePositions,
        oldClip.translatedPhrases ?? [],
        survivingPhrases,
      ),
    };
  }

  /**
   * Gets the default karaoke config for new clips
   */
  private getDefaultKaraokeConfig(): KaraokeConfig {
    // Get default config from editor
    const defaultConfig = this.editor.getDefaultKaraokeConfig();
    if (defaultConfig) {
      return defaultConfig;
    }

    throw new Error(
      'No karaoke configuration available for creating new clips',
    );
  }

  /**
   * Merges orphaned replacement clips into the following clip.
   *
   * Detects clips where ALL tokens have state='added', indicating they are
   * from a replacement operation and should be integrated into the following clip.
   *
   * Only avoids merging across significant time gaps (>1s) to preserve
   * intentional breaks while allowing merges across small pauses.
   */
  private mergeIsolatedClipsIntoFollowing(
    clips: KaraokeClip[],
    newOrExtendedClipIds: string[],
  ): void {
    const NATURAL_BEAT_DURATION_MS = 1000; // Maximum gap to allow merging

    let i = 0;
    while (i < clips.length) {
      const clip = clips[i];

      // Only check new clips
      const isNewClip = newOrExtendedClipIds.includes(clip.id);
      if (!isNewClip) {
        i++;
        continue;
      }

      // Check if ALL tokens have state='added' (orphaned replacement)
      // We don't do 'replaced' state in headless
      const allTokensAdded = clip.transcriptTokens.every(
        token => token.state === 'added',
      );

      if (!allTokensAdded) {
        i++;
        continue;
      }

      const nextClip = i < clips.length - 1 ? clips[i + 1] : null;

      if (!nextClip) {
        i++;
        continue;
      }

      // Check gap between current and next clip
      const clipEnd = clip.getKaraokeEndTime();
      const nextStart = nextClip.getKaraokeStartTime();

      if (clipEnd === null || nextStart === null) {
        i++;
        continue;
      }

      const gapMs = (nextStart - clipEnd) * 1000;

      // Don't merge if there's a significant time gap (>1s)
      if (gapMs > NATURAL_BEAT_DURATION_MS) {
        i++;
        continue;
      }

      // Merge current clip into next (prepend)
      nextClip.prependClip(clip);

      // Mark target as extended for re-segmentation
      if (!newOrExtendedClipIds.includes(nextClip.id)) {
        newOrExtendedClipIds.push(nextClip.id);
      }

      // Remove the prepended clip's ID from the list since it no longer exists
      const prependedClipIdIndex = newOrExtendedClipIds.indexOf(clip.id);
      if (prependedClipIdIndex !== -1) {
        newOrExtendedClipIds.splice(prependedClipIdIndex, 1);
      }

      // Remove current clip
      clips.splice(i, 1);
      // Don't increment i since we removed current element
      continue;
    }
  }

  /**
   * Splits clips that are new or extended into smaller segments using karaokeSegmentation
   */
  private splitClipsIntoSegments(
    clips: KaraokeClip[],
    clipIdsToSplit: string[],
  ): void {
    const clipIdsSet = new Set(clipIdsToSplit);

    // Process clips in reverse order to safely replace them
    for (let i = clips.length - 1; i >= 0; i--) {
      const clip = clips[i];

      if (!clipIdsSet.has(clip.id)) {
        continue; // Skip clips that don't need splitting
      }

      // Never re-segment a translated row here: `segmentTokens` measures the
      // ORIGINAL words (the producer segments translated captions by phrase
      // instead), and the sub-clips it builds carry no translated text — so
      // splitting would revert the row to the original language. An extended
      // translated row stays one row until a caption-text sync re-texts it.
      if (clip.translatedText != null) {
        continue;
      }

      const punctuationLineBreak = resolvePunctuationLineBreak(
        clip.karaokeConfig?.line_break_style,
      );

      // Segment the clip's tokens
      const segments = segmentTokens(clip.transcriptTokens, {
        // PLAT-11481 — same sentinel normalization as the producer, so an
        // edit-time re-split agrees with the produce-time line length.
        maxSegmentCharLength: resolveConfiguredMaxCharLength(
          clip.karaokeConfig?.transcript_maximum_length,
        ),
        punctuationLineBreak,
      });

      // If segmentation produced only one segment, no splitting needed
      if (segments.length <= 1) {
        continue;
      }

      // Remove the original clip
      clips.splice(i, 1);

      // Create new clips from segments and insert at the same position
      const newClips: KaraokeClip[] = [];
      for (const segment of segments) {
        if (segment.tokens.length === 0) {
          continue;
        }

        // Identify hidden tokens from the original clip
        const hiddenTokenIds = clip.hiddenTranscriptTokenIds.filter(id =>
          segment.tokens.some(t => t.id === id),
        );

        // Create new clip from segment
        const newClip = new KaraokeClip({
          id: IdGenerator.shortRandomId(),
          transcriptTokens: segment.tokens,
          hiddenTranscriptTokenIds: hiddenTokenIds,
          ignoredByFiltersIds: [],
          karaokeConfig: clip.karaokeConfig,
          track: clip.track,
          startSecondsOffset: undefined,
          endSecondsOffset: undefined,
        });

        newClips.push(newClip);
      }

      // Squash orphaned clips within the new clips before inserting. The cap
      // (same normalization as the split above, PLAT-11481: the literal 100
      // sentinel means "default") is passed through so a merge that would
      // overflow the configured line length is refused rather than skipping
      // squashing altogether — which used to strand every short sentence tail
      // on its own row whenever a custom length was set.
      // Punctuation style keeps every clause row, as the producer does.
      const squashedClips = punctuationLineBreak
        ? newClips
        : squashOrphanedSegments(
            newClips,
            resolveConfiguredMaxCharLength(
              clip.karaokeConfig?.transcript_maximum_length,
            ),
          );

      // Each soft break follows its token to the row that now holds it —
      // after squashing, which merges rows in place.
      for (const squashed of squashedClips) {
        squashed.softBreakAfterTokenIds = keepTokenSoftBreaks(
          clip.softBreakAfterTokenIds,
          squashed.transcriptTokens,
        );
      }

      // Insert squashed clips at the original position
      clips.splice(i, 0, ...squashedClips);
    }
  }
}
